import { Route, Switch, useRouteMatch } from "react-router-dom";
import { QueryLoader } from "@jsq/ds/core";
import { withClientContext } from "~/apollo/client";
import { useGetNestedOwnershipFeatureFlagQuery } from "~/graphql/mainHooks";
import { useGetProfilesForCaseQuery } from "~/graphql/supergraphHooks";
import { type GetComplianceCaseQuery } from "~/graphql/supergraphTypes";
import { ProposeChangePreview } from "../../documents/ProposeChangePreview";
import { useIOReusableProfiles } from "../../flags";
import { ProposedChangesModal } from "../../ProposedChangesModal";
import { ProposedChangesReview } from "../../ProposedChangesReview";
import { isRecurringScreeningComplianceCase } from "../utils/isRecurringScreeningCase";
import { ProfilesTreeCard } from "./ProfilesTreeCard";

interface Props {
  caseId: number;
  programId: number;
  complianceCase?: GetComplianceCaseQuery["complianceCase"];
}
const Profiles = ({ caseId, programId, complianceCase }: Props) => {
  const match = useRouteMatch();
  const ioReusableProfilesEnabled = useIOReusableProfiles();
  const profileDetailsResults = useGetProfilesForCaseQuery({
    variables: {
      caseId: caseId,
      ioReusableProfilesEnabled,
    },
    // Using network-only instead of no-cache ensures fresh data while allowing proper caching
    fetchPolicy: "network-only",
  });
  const showNestedOwnership = useGetNestedOwnershipFeatureFlagQuery({
    context: withClientContext("staff"),
  });

  const allowNestedOwnership =
    showNestedOwnership.data?.devFeatures.showComplianceCanopyNestedOwnership ?? false;

  const isRecurringScreeningCase = isRecurringScreeningComplianceCase(
    profileDetailsResults.data?.complianceCase?.caseType,
  );

  return (
    <QueryLoader result={profileDetailsResults}>
      <ProfilesTreeCard
        complianceCase={profileDetailsResults.data?.complianceCase}
        caseId={caseId}
        programId={programId}
        readonly={
          profileDetailsResults.data?.complianceCase?.readonly || isRecurringScreeningCase
        }
        showNestedOwnership={allowNestedOwnership}
        showScreeningColumns
        fullComplianceCase={complianceCase}
      />
      <Switch>
        <Route
          path={`${match.path}/:sourceProfile/proposed-changes/:targetProfile/documents`}
          exact
          render={() => <ProposeChangePreview />}
        />
        <Route
          path={`${match.path}/:sourceProfile/proposed-changes/:targetProfile/review`}
          exact
          render={() => <ProposedChangesReview />}
        />
        <Route
          path={`${match.path}/:sourceProfile/proposed-changes/:targetProfile`}
          render={() => <ProposedChangesModal />}
        />
      </Switch>
    </QueryLoader>
  );
};

export default Profiles;
